<template>
  <div class="bg-white text-[#202632]">
    <MediaHubHeader />

    <section class="relative h-[470px] w-full overflow-hidden bg-[#e9e9e9] sm:h-[570px] lg:h-[690px]">
      <img :src="currentHeroImage" :alt="currentHero?.altText || ''" class="absolute inset-0 h-full w-full object-cover" />
      <div class="absolute inset-0 bg-gradient-to-b from-black/0 to-black/70" />
      <div
        class="absolute bottom-10 left-1/2 flex w-full max-w-[1140px] -translate-x-1/2 flex-col gap-8 px-4 sm:bottom-16 lg:bottom-[110px] lg:px-0"
      >
        <div class="flex max-w-[850px] flex-col items-start gap-4">
          <p class="text-2xl font-bold leading-[1.2] text-white sm:text-4xl lg:text-[50px]">
            {{ currentHeroTitle }}
          </p>
          <p class="text-sm font-medium leading-[1.6] text-white sm:text-lg lg:text-xl">
            {{ currentHeroSubtitle }}
          </p>
          <a href="#media-contents" class="rounded-lg bg-[#d9a72e] px-5 py-3 text-sm font-semibold text-white sm:px-7 sm:text-base">
            콘텐츠 보러가기
          </a>
        </div>
      </div>
      <div v-if="mainBanners.length > 1" class="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
        <button
          v-for="(banner, index) in mainBanners"
          :key="banner.mediaHubMainBannerCode || index"
          type="button"
          class="h-2.5 rounded-full transition-all"
          :class="index === activeBannerIndex ? 'w-8 bg-[#d9a72e]' : 'w-2.5 bg-white/70'"
          :aria-label="`${index + 1}번 배너 보기`"
          @click="setActiveBanner(index)"
        />
      </div>
    </section>

    <main id="media-contents" class="relative z-10 -mt-[50px] overflow-hidden rounded-tl-[50px] rounded-tr-[50px] bg-white">
      <section class="flex w-full justify-center px-4 pb-8 pt-[50px]">
        <div class="flex w-full max-w-[1140px] items-center gap-2 overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.code"
            type="button"
            class="shrink-0 rounded-lg px-4 py-2 text-base transition-colors sm:px-7 sm:py-2.5 sm:text-lg"
            :class="tabs[0]?.code === tab.code ? 'border border-[#d9a72e] font-bold text-[#d9a72e]' : 'text-[#3a4250] hover:bg-[#f6f6f6]'"
            @click="scrollToCategory(tab.code)"
          >
            {{ tab.name }}
          </button>
        </div>
      </section>

      <section v-if="loading" class="px-4 py-20 text-center text-[#5f6673]">
        미디어허브 콘텐츠를 불러오고 있습니다.
      </section>

      <section v-else-if="errorMessage" class="px-4 py-20 text-center">
        <p class="font-semibold text-[#202632]">콘텐츠를 불러오지 못했습니다.</p>
        <p class="mt-2 text-sm text-[#8a8f99]">{{ errorMessage }}</p>
      </section>

      <section v-else-if="visibleSections.length === 0" class="px-4 py-20 text-center text-[#5f6673]">
        현재 노출 중인 미디어허브 콘텐츠가 없습니다.
      </section>

      <section
        v-for="section in visibleSections"
        :key="section.mediaHubDisplayCategoryCode"
        :id="categorySectionId(section.mediaHubDisplayCategoryCode)"
        class="flex w-full flex-col items-center gap-6 bg-white px-4 py-10 sm:py-12 lg:gap-9 lg:py-14"
      >
        <div class="flex w-full max-w-[1140px] items-end justify-between gap-4">
          <h2 class="text-xl font-bold leading-[1.4] text-[#202632] sm:text-2xl lg:text-[28px]">
            {{ section.displayCategoryName }}
          </h2>
          <router-link
            :to="{ name: 'media-category', params: { categoryCode: section.mediaHubDisplayCategoryCode } }"
            class="shrink-0 rounded-full border border-[#d9a72e] px-4 py-2 text-sm font-bold text-[#d9a72e] transition hover:bg-[#d9a72e] hover:text-white"
          >
            전체보기
          </router-link>
        </div>

        <div v-if="section.contents?.length" class="relative w-full max-w-[1140px]">
          <button
            v-if="section.contents.length > 3"
            type="button"
            class="slider-button slider-button-left"
            :disabled="!canSlidePrev(section)"
            aria-label="이전 콘텐츠 보기"
            @click="slideCategory(section, -1)"
          >
            ‹
          </button>
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <a
            v-for="content in visibleCategoryContents(section)"
            :key="content.homepageContentCode"
            :href="contentGoUrl(content.homepageContentCode)"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex min-h-[360px] flex-col overflow-hidden rounded-xl bg-white shadow-[4px_4px_14px_rgba(0,0,0,0.14)] transition hover:-translate-y-1 hover:shadow-[4px_8px_24px_rgba(0,0,0,0.2)]"
          >
            <div class="relative h-[204px] w-full overflow-hidden bg-[#e9e9e9]">
              <img :src="content.thumbnailUrl || fallbackImage(content)" alt="" class="h-full w-full object-cover transition group-hover:scale-105" />
              <span class="absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
                {{ contentTypeLabel(content.contentType) }}
              </span>
            </div>
            <div class="flex flex-1 flex-col gap-3 p-5">
              <h3 class="line-clamp-2 text-lg font-bold leading-[1.45] text-[#3a4250] sm:text-xl">
                {{ content.title }}
              </h3>
              <p v-if="content.description" class="line-clamp-2 text-sm leading-[1.6] text-[#6b7280]">
                {{ content.description }}
              </p>
              <div class="mt-auto flex flex-wrap items-center gap-2 text-sm text-[#8a8f99]">
                <span>{{ content.authorName || content.mediaSourceName || content.mediaHubCategoryName }}</span>
                <span v-if="formatDate(content.publishedDt || content.fetchedDt)">·</span>
                <span>{{ formatDate(content.publishedDt || content.fetchedDt) }}</span>
              </div>
            </div>
          </a>
          </div>
          <button
            v-if="section.contents.length > 3"
            type="button"
            class="slider-button slider-button-right"
            :disabled="!canSlideNext(section)"
            aria-label="다음 콘텐츠 보기"
            @click="slideCategory(section, 1)"
          >
            ›
          </button>
        </div>
        <div v-else class="w-full max-w-[1140px] rounded-xl border border-[#e0e0e0] bg-[#f9f9f9] px-6 py-10 text-center text-sm text-[#6b7280] sm:text-base">
          현재 노출 중인 미디어허브 콘텐츠가 없습니다.
        </div>
      </section>
    </main>

    <footer class="flex w-full flex-col items-center bg-[#f6f6f6] px-4 py-10 sm:py-[60px]">
      <div class="flex w-full max-w-[1140px] flex-col gap-6">
        <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div class="text-lg font-bold leading-[1.5] text-[#202632] sm:text-xl lg:text-2xl">
            <p>금나라의 주요 미디어 콘텐츠를 한 곳에서 확인하세요.</p>
          </div>
          <div class="flex flex-wrap gap-4 text-sm text-[#4e5968] sm:text-base">
            <span v-for="link in footerLinks" :key="link">{{ link }}</span>
          </div>
        </div>
        <div class="h-px w-full bg-[#d9d9d9]" />
        <div class="text-sm leading-[1.6] text-[#8a8f99] sm:text-base">
          <p>(주)골드나라 대표이사 : 배재한</p>
          <p>사업자등록번호 : 573-81-03483</p>
          <p>Copyright AURA. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { getMediaHubContentGoUrl, getMediaHubHome } from '@/api/mediaHub'
import MediaHubHeader from '@/components/MediaHubHeader.vue'
import mainVisual from '@/assets/figma/home/main_visual.png'
import imgImage64 from '@/assets/figma/home/imgImage64.png'
import imgImage65 from '@/assets/figma/home/imgImage65.png'
import imgImage66 from '@/assets/figma/home/imgImage66.png'

const fallbackImages = [imgImage64, imgImage65, imgImage66]

export default {
  name: 'HomeView',
  components: { MediaHubHeader },
  data() {
    return {
      loading: false,
      errorMessage: '',
      categories: [],
      categorySlideOffsets: {},
      mainBanners: [],
      activeBannerIndex: 0,
      bannerTimer: null,
      footerLinks: ['회사소개', '실시간 거래 시세', '미디어 허브', '중개거래'],
    }
  },
  computed: {
    tabs() {
      return this.categories.map((category) => ({
        code: category.mediaHubDisplayCategoryCode,
        name: category.displayCategoryName,
      }))
    },
    visibleSections() {
      return this.categories
    },
    currentHero() {
      return this.mainBanners[this.activeBannerIndex] || null
    },
    currentHeroImage() {
      return this.currentHero?.imageUrl || mainVisual
    },
    currentHeroTitle() {
      return this.currentHero?.bannerTitle || '골드나라 미디어허브'
    },
    currentHeroSubtitle() {
      return this.currentHero?.bannerSubtitle || '유튜브, 블로그, 네이버TV 등 골드나라의 주요 콘텐츠를 한 곳에서 확인하세요.'
    },
  },
  mounted() {
    this.loadContents()
  },
  beforeUnmount() {
    this.clearBannerTimer()
  },
  methods: {
    async loadContents() {
      this.loading = true
      this.errorMessage = ''
      try {
        const data = await getMediaHubHome({ area: 'MEDIA_HUB' })
        this.mainBanners = data.mainBanners || []
        this.categories = (data.categories || []).filter((category) => category.displayCategoryName !== '전체')
        this.categorySlideOffsets = {}
        this.activeBannerIndex = 0
        this.scheduleNextBanner()
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.loading = false
      }
    },
    setActiveBanner(index) {
      this.activeBannerIndex = index
      this.scheduleNextBanner()
    },
    scheduleNextBanner() {
      this.clearBannerTimer()
      if (this.mainBanners.length <= 1) return
      const seconds = Number(this.currentHero?.displaySeconds || 30)
      this.bannerTimer = window.setTimeout(() => {
        this.activeBannerIndex = (this.activeBannerIndex + 1) % this.mainBanners.length
        this.scheduleNextBanner()
      }, Math.max(1, seconds) * 1000)
    },
    clearBannerTimer() {
      if (this.bannerTimer) {
        window.clearTimeout(this.bannerTimer)
        this.bannerTimer = null
      }
    },
    visibleCategoryContents(section) {
      const contents = section.contents || []
      const start = this.categorySlideOffsets[section.mediaHubDisplayCategoryCode] || 0
      return contents.slice(start, start + 3)
    },
    canSlidePrev(section) {
      return (this.categorySlideOffsets[section.mediaHubDisplayCategoryCode] || 0) > 0
    },
    canSlideNext(section) {
      const contents = section.contents || []
      const start = this.categorySlideOffsets[section.mediaHubDisplayCategoryCode] || 0
      return start < Math.max(0, contents.length - 3)
    },
    slideCategory(section, direction) {
      const code = section.mediaHubDisplayCategoryCode
      const contents = section.contents || []
      const current = this.categorySlideOffsets[code] || 0
      const maxOffset = Math.max(0, contents.length - 3)
      const next = Math.min(maxOffset, Math.max(0, current + direction))
      this.categorySlideOffsets = {
        ...this.categorySlideOffsets,
        [code]: next,
      }
    },
    contentTypeLabel(type) {
      const labels = {
        NEWS: '뉴스',
        VIDEO: '동영상',
        SHORTS: '쇼츠',
        CLIP: '클립',
        BLOG: '블로그',
        CAFE_ARTICLE: '카페글',
        PRODUCT: '상품정보',
        NOTICE: '공지/이벤트',
        LIVE: '라이브',
        POST: '게시글',
        ETC: '기타',
      }
      return labels[type] || type || '콘텐츠'
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date)
    },
    fallbackImage(content) {
      const key = content.homepageContentCode || content.fetchCode || ''
      const index = Math.abs([...key].reduce((sum, char) => sum + char.charCodeAt(0), 0)) % fallbackImages.length
      return fallbackImages[index]
    },
    contentGoUrl(homepageContentCode) {
      return getMediaHubContentGoUrl(homepageContentCode)
    },
    categorySectionId(categoryCode) {
      return `media-category-${categoryCode}`
    },
    scrollToCategory(categoryCode) {
      const target = document.getElementById(this.categorySectionId(categoryCode))
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
  },
}
</script>

<style scoped>
.slider-button {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: flex;
  width: 44px;
  height: 44px;
  transform: translateY(-50%);
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(217, 167, 46, 0.45);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.16);
  color: #b38314;
  font-size: 34px;
  font-weight: 700;
  line-height: 1;
  transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease, transform 0.2s ease;
}

.slider-button:hover:not(:disabled) {
  transform: translateY(-50%) scale(1.04);
  background: #d9a72e;
  color: #fff;
}

.slider-button:disabled {
  cursor: default;
  opacity: 0.35;
}

.slider-button-left {
  left: -22px;
}

.slider-button-right {
  right: -22px;
}

@media (max-width: 1199px) {
  .slider-button-left {
    left: 8px;
  }

  .slider-button-right {
    right: 8px;
  }
}
</style>
